import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  toggleButtonGroupClasses,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { transformPixels2Robot, transformRobot2Pixels } from '../Utils';
import HotKeyToggleButton from '../layoutsView/HotKeyToggleButton';

const AnnotationsTools = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    borderRadius: '4px',
    border: 0,
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
  {
    marginLeft: -1,
    borderLeft: '1px solid transparent',
  },
}));

const mappingTools = [
  {
    drag: {
      label: 'Move',
      hotKey: '1',
      icon: <IconComponent iconName="hand-left" style={{ fontSize: '24px' }} />,
      can_disable: false,
    },
    draw: {
      label: 'Draw',
      hotKey: '2',
      icon: (
        <IconComponent iconName="square-outline" style={{ fontSize: '24px' }} />
      ),
      can_disable: true,
    },
    delete: {
      label: 'Delete Box',
      hotKey: '3',
      icon: <IconComponent iconName="trash" style={{ fontSize: '24px' }} />,
      can_disable: true,
    },
  },
  {
    drag: {
      label: 'Move',
      hotKey: '1',
      icon: <IconComponent iconName="hand-left" style={{ fontSize: '24px' }} />,
      can_disable: false,
    },
  },
  {
    drag: {
      label: 'Move',
      hotKey: '1',
      icon: <IconComponent iconName="hand-left" style={{ fontSize: '24px' }} />,
      can_disable: true,
    },
  },
];

export default function MapComponent(props) {
  const {
    canvasId,
    mapInfo,
    selectedJob,
    setSelectedJobs,
    tabIndex = 0,
    mode,
    storeInfo,
    robotFloor,
    isLoadingBucketMapLayout,
    postMappingJob,
    postMappingJobResponse,
    patchMappingJob,
    mapMetadata,
    isLoadingJobImg,
  } = props;
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const { t } = useTranslation();

  const [mapImg, setMapImg] = useState(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [bounds, setBounds] = useState({ minX: 0, minY: 0, maxX: 0, maxY: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [startDragPosition, setStartDragPosition] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [startDrawingPosition, setStartDrawingPosition] = useState({
    x: 0,
    y: 0,
  });
  const [zones, setZones] = useState([]);
  const [activeTool, setActiveTool] = useState(null);
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [openDeclineModal, setOpenDeclineModal] = useState(false);
  const [BboxDisplay, setBboxDisplay] = useState(true);
  const [declineOption, setDeclineOption] = useState('rejected');
  const [scale, setScale] = useState(1);

  const scaleFactor = 0.05;

  const handleWheel = (e) => {
    e.preventDefault();
    setScale((prevScale) => {
      let newScale = prevScale + (e.deltaY < 0 ? scaleFactor : -scaleFactor);
      return Math.min(Math.max(newScale, 0.5), 3); // zoom between 0.5x and 3x
    });
  };

  useEffect(() => {
    if (!selectedJob?.mapping_job_id) setZones([]);
  }, [selectedJob]);

  useEffect(() => {
    if (mapInfo && mapMetadata && selectedJob && selectedJob?.job_content) {
      const job_zones = selectedJob.job_content.zones;
      const jobs_to_draw = job_zones.map((zone) => {
        const [rx0, ry0] = zone.bbox[0];
        const [rx1, ry1] = zone.bbox[1];
        const [px0, py0, angle0] = transformRobot2Pixels(
          rx0,
          ry0,
          0,
          mapMetadata.origin,
          mapMetadata.resolution,
          height
        );
        const [px1, py1, angle1] = transformRobot2Pixels(
          rx1,
          ry1,
          0,
          mapMetadata.origin,
          mapMetadata.resolution,
          height
        );
        const x_min = Math.min(px0, px1);
        const y_min = Math.min(py0, py1);
        const x_max = Math.max(px0, px1);
        const y_max = Math.max(py0, py1);
        const zone_object = {
          remapping_zone_id: zone.remapping_zone_id,
          x0: x_min + imagePosition.x,
          y0: y_min + imagePosition.y,
          x1: x_max + imagePosition.x,
          y1: y_max + imagePosition.y,
          width: x_max - x_min,
          height: y_max - y_min,
          accepted: zone.accepted,
        };
        return zone_object;
      });
      setZones(jobs_to_draw);
    }
  }, [mapInfo, mapMetadata, height]);

  useEffect(() => {
    if (postMappingJobResponse?.code === 200) {
      setZones([]);
    }
  }, [postMappingJobResponse]);

  useEffect(() => {
    if (mapInfo) {
      const container = containerRef.current?.getBoundingClientRect();
      if (container)
        setBounds({
          minX: container.width - mapInfo.width,
          minY: container.height - mapInfo.height,
          maxX: 0,
          maxY: 0,
        });
      setImagePosition({ x: 0, y: 0 });
      if (Object.keys(mapInfo).length) {
        setHeight(mapInfo.height);
        setWidth(mapInfo.width);
        setMapImg(mapInfo.map_img);
      }
    }
  }, [mapInfo]);

  // Redraws rectangles when the image is moved or new ones are added
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.font = `${14 * scale}px Arial`;
    ctx.fillStyle = 'black';

    if (BboxDisplay)
      zones.forEach((rect) => {
        // check condition
        let is_accepted = rect?.accepted;
        if (is_accepted) ctx.strokeStyle = 'green';
        else if (is_accepted === null) ctx.strokeStyle = 'yellow';
        else ctx.strokeStyle = 'red';
        ctx.lineWidth = 2;

        ctx.strokeRect(
          rect.x0 * scale + imagePosition.x,
          rect.y0 * scale + imagePosition.y,
          rect.width * scale,
          rect.height * scale
        );

        const statusText = rect.accepted
          ? 'Accepted'
          : rect.accepted === false
            ? 'Rejected'
            : 'Pending';

        ctx.fillText(
          statusText,
          rect.x0 * scale + imagePosition.x,
          rect.y0 * scale + imagePosition.y - 5
        );
      });
  }, [imagePosition, zones, BboxDisplay, scale]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left - imagePosition.x) / scale;
    const mouseY = (e.clientY - rect.top - imagePosition.y) / scale;

    if (mode === 'review') {
      const updatedZones = zones.map((zone) => {
        const [x1, y1] = [zone.x0, zone.y0];
        const [x2, y2] = [zone.x1, zone.y1];

        const x_min = Math.min(x1, x2);
        const y_min = Math.min(y1, y2);
        const x_max = Math.max(x1, x2);
        const y_max = Math.max(y1, y2);

        if (
          mouseX >= x_min &&
          mouseX <= x_max &&
          mouseY >= y_min &&
          mouseY <= y_max
        ) {
          return { ...zone, accepted: !zone.accepted };
        }
        return zone;
      });
      setZones(updatedZones);
    }
  };

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (activeTool === 'drag') {
      setIsDragging(true);
      setStartDragPosition({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y,
      });
    } else if (activeTool === 'draw') {
      setIsDrawing(true);
      setStartDrawingPosition({ x: mouseX, y: mouseY });
    } else if (activeTool === 'delete') {
      let clickX = (mouseX - imagePosition.x) / scale;
      let clickY = (mouseY - imagePosition.y) / scale;
      let filtered = zones.filter(
        (item) =>
          clickX < Math.min(item.x0, item.x1) ||
          clickX > Math.max(item.x0, item.x1) ||
          clickY < Math.min(item.y0, item.y1) ||
          clickY > Math.max(item.y0, item.y1)
      );
      setZones(filtered);
    }
  };

  const handleMouseMove = (e) => {
    if (activeTool === 'drag' && isDragging) {
      const newX = e.clientX - startDragPosition.x;
      const newY = e.clientY - startDragPosition.y;

      setImagePosition({
        x: Math.max(bounds.minX, Math.min(bounds.maxX, newX)),
        y: Math.max(bounds.minY, Math.min(bounds.maxY, newY)),
      });
    } else if (activeTool === 'draw' && isDrawing) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      zones.forEach((rect) => {
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 2;
        ctx.strokeRect(
          rect.x0 * scale + imagePosition.x,
          rect.y0 * scale + imagePosition.y,
          rect.width * scale,
          rect.height * scale
        );
      });

      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.strokeRect(
        startDrawingPosition.x,
        startDrawingPosition.y,
        mouseX - startDrawingPosition.x,
        mouseY - startDrawingPosition.y
      );
    }
  };

  const handleMouseUp = (e) => {
    if (activeTool === 'draw' && isDrawing) {
      const rect = canvasRef.current.getBoundingClientRect();

      const mouseX = (e.clientX - rect.left - imagePosition.x) / scale;
      const mouseY = (e.clientY - rect.top - imagePosition.y) / scale;
      const startX = (startDrawingPosition.x - imagePosition.x) / scale;
      const startY = (startDrawingPosition.y - imagePosition.y) / scale;

      // Save the rectangle in the state in coordinates relative to the map
      setZones((prev) => [
        ...prev,
        {
          remapping_zone_id: zones.length + 1,
          x0: Math.min(startX, mouseX),
          y0: Math.min(startY, mouseY),
          x1: Math.max(startX, mouseX),
          y1: Math.max(startY, mouseY),
          width: Math.abs(mouseX - startX),
          height: Math.abs(mouseY - startY),
          accepted: null,
        },
      ]);
    }
    setIsDragging(false);
    setIsDrawing(false);
  };

  const onChange = (event, newValue) => {
    setActiveTool(newValue);
  };

  const onClose = () => setOpenSaveModal(false);

  const onSave = () => {
    setOpenSaveModal(false);
    let parsedZones = {
      zones: zones.map((zone) => {
        return {
          remapping_zone_id: zone.remapping_zone_id,
          accepted: zone.accepted,
          bbox: [
            [zone.x0, zone.y0],
            [zone.x1, zone.y1],
          ],
        };
      }),
    };
    if (selectedJob) {
      updateMappingJobState(selectedJob.mapping_job_id, {
        job_content: parsedZones,
      });
    } else {
      // transform zones from pixels to meters
      parsedZones.zones.forEach((zone) => {
        let [x0, y0] = zone.bbox[0];
        let [x1, y1] = zone.bbox[1];
        let [rx0, ry0] = transformPixels2Robot(
          x0,
          y0,
          mapMetadata.origin,
          mapMetadata.resolution,
          height
        );
        let [rx1, ry1] = transformPixels2Robot(
          x1,
          y1,
          mapMetadata.origin,
          mapMetadata.resolution,
          height
        );
        zone.bbox = [
          [rx0, ry0],
          [rx1, ry1],
        ];
      });
      const metadata = {
        origin: mapMetadata.origin,
        resolution: mapMetadata.resolution,
      };
      postMappingJob(storeInfo?.store_id, robotFloor, parsedZones, metadata);
    }
  };

  const onDecline = () => {
    setOpenDeclineModal(false);
    if (declineOption === 'reject')
      updateMappingJobState(selectedJob.mapping_job_id, {
        job_state: 'Cancelled',
      });
    else if (declineOption === 'pending') {
      let parsedZones = {
        zones: zones.map((zone) => {
          return {
            remapping_zone_id: zone.remapping_zone_id,
            accepted: zone.accepted,
            bbox: [
              [zone.x0, zone.y0],
              [zone.x1, zone.y1],
            ],
          };
        }),
      };
      updateMappingJobState(selectedJob.mapping_job_id, {
        job_content: parsedZones,
        job_state: 'Pending',
      });
    }
    setSelectedJobs((prev) => {
      const newList = [...prev];
      newList[tabIndex] = null;
      return newList;
    });
    setMapImg(null);
  };

  const updateMappingJobState = (job_id, values) => {
    values['job_id'] = job_id;
    if (Object.keys(values).includes('job_content')) {
      // transform pixels to meters
      values.job_content.zones.forEach((zone) => {
        let [x0, y0] = zone.bbox[0];
        let [x1, y1] = zone.bbox[1];
        let [rx0, ry0] = transformPixels2Robot(
          x0,
          y0,
          mapMetadata.origin,
          mapMetadata.resolution,
          height
        );
        let [rx1, ry1] = transformPixels2Robot(
          x1,
          y1,
          mapMetadata.origin,
          mapMetadata.resolution,
          height
        );
        zone.bbox = [
          [rx0, ry0],
          [rx1, ry1],
        ];
      });
      values['job_content'] = JSON.stringify(values['job_content']);
    }
    if (Object.keys(values).includes('map_metadata')) {
      values['map_metadata'] = JSON.stringify(values['map_metadata']);
    }
    patchMappingJob(values);
  };

  const handleCheckButton = () => {
    let newValues = {};
    switch (mode) {
      case 'pending':
        newValues = {
          job_state: 'In Review',
        };
        break;
      case 'review':
        let parsedZones = {
          zones: zones.map((zone) => {
            return {
              remapping_zone_id: zone.remapping_zone_id,
              accepted: zone.accepted,
              bbox: [
                [zone.x0, zone.y0],
                [zone.x1, zone.y1],
              ],
            };
          }),
        };
        newValues = {
          job_content: parsedZones,
          job_state: 'Accepted',
        };
        break;
      default:
        break;
    }
    updateMappingJobState(selectedJob.mapping_job_id, newValues);
  };

  const handleDialogContent = () => {
    let title, message;
    if (selectedJob) {
      title = t('overseer_app.mapping.update_job', 'Update Mapping Job');
      message = t(
        'overseer_app.mapping.update_job_description',
        'You are about to update a mapping job. Do you want to continue?'
      );
    } else {
      title = t(
        'overseer_app.mapping.create_job_confirm',
        'New Mapping Job Confirm'
      );
      message = t(
        'overseer_app.mapping.create_job_description',
        `You are about to create a mapping job with ${zones.length} zones. Do you want to continue?`,
        { nZones: zones.length }
      );
    }
    return (
      <>
        <DialogTitle sx={{ marginBottom: -1 }}>{title}</DialogTitle>
        <DialogContent>{message}</DialogContent>
      </>
    );
  };

  const confirmActions = () => {
    let actions;
    switch (mode) {
      case 'pending':
        actions = (
          <IconButton
            disabled={zones.length === 0}
            onClick={() => setOpenSaveModal(true)}
          >
            <SaveIcon />
          </IconButton>
        );
        break;

      case 'review':
        actions = (
          <>
            <IconButton
              disabled={
                zones.length === 0 || zones.some((z) => z.accepted === null)
              }
              onClick={() => setOpenDeclineModal(true)}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              disabled={
                !selectedJob ||
                zones.length === 0 ||
                zones.some((z) => !z.accepted)
              }
              onClick={handleCheckButton}
            >
              <CheckIcon />
            </IconButton>
          </>
        );
        break;
      case 'submit':
        actions = (
          <>
            <Tooltip title="TBD">
              <span>
                <IconButton disabled>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="TBD">
              <span>
                <IconButton disabled>
                  <UploadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="TBD">
              <span>
                <IconButton disabled onClick={() => setOpenSaveModal(true)}>
                  <CloudUploadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
        break;
      default:
        break;
    }
    return actions;
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          boxShadow: (theme) => theme.shadows[1],
          justifyContent: 'space-between',
        }}
      >
        <Grid
          container
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Grid item>
            <AnnotationsTools
              value={activeTool}
              exclusive
              onChange={onChange}
              aria-label="annotations tools"
            >
              {Object.entries(mappingTools[tabIndex]).map(
                ([tool, { label, hotKey, icon, can_disable, exclusive }]) => {
                  return (
                    <HotKeyToggleButton
                      key={tool}
                      value={tool}
                      aria-label={label}
                      hotKey={hotKey}
                      label={label}
                      exclusive={exclusive}
                      disabled={!mapImg}
                    >
                      {icon}
                    </HotKeyToggleButton>
                  );
                }
              )}
              <Box
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  position: 'relative',
                }}
              >
                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{ mx: 0.5, my: 1 }}
                />
              </Box>
            </AnnotationsTools>
            <ToggleButton
              value="check"
              selected={BboxDisplay}
              onChange={() => setBboxDisplay((prevSelected) => !prevSelected)}
              sx={{
                border: 'none',
                opacity: 0.5,
              }}
            >
              <IconComponent iconName="eye" style={{ fontSize: '24px' }} />
            </ToggleButton>
          </Grid>
          <Grid item>
            {storeInfo ? (
              <Typography variant="body1" color="text.secondary">
                {`${storeInfo.store_id} - ${storeInfo.name} / ${t(
                  'overseer_app.general.floor',
                  'Floor'
                )} ${robotFloor}`}
              </Typography>
            ) : null}
          </Grid>
          <Grid item>{confirmActions()}</Grid>
        </Grid>
      </Paper>

      <Card sx={{ height: '90%', width: '100%', pb: 0, position: 'relative' }}>
        {isLoadingBucketMapLayout || isLoadingJobImg ? (
          <LinearProgress sx={{ width: '95%', mx: 'auto' }} color="secondary" />
        ) : (
          <CardContent
            id={'map-container'}
            ref={containerRef}
            sx={{
              height: '100%',
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 'inherit',
              padding: 0,
              margin: 0,
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onClick={handleCanvasClick}
            onWheel={handleWheel}
          >
            {!mapImg ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#F9F9F9',
                  border: '1px solid #DDD',
                  borderRadius: '5px',
                }}
              >
                <Typography variant="h6" sx={{ color: 'grey' }}>
                  No map available
                </Typography>
              </Box>
            ) : (
              <img
                ref={imgRef}
                src={'data:image/jpeg;base64,' + mapImg}
                alt="Bucket Map Layout"
                style={{
                  position: 'absolute',
                  top: imagePosition.y,
                  left: imagePosition.x,
                  zIndex: 1,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  userSelect: 'none',
                  cursor: activeTool === 'drag' ? 'grab' : 'default',
                }}
              />
            )}
            <canvas
              id={canvasId}
              ref={canvasRef}
              width={width}
              height={height}
              style={{
                zIndex: 2,
                position: 'absolute',
                top: 0,
                left: 0,
                userSelect: 'none',
              }}
            />
          </CardContent>
        )}
      </Card>
      <Dialog open={openSaveModal} onClose={onClose} fullWidth maxWidth="sm">
        {handleDialogContent()}

        <DialogActions>
          <Button color="secondary" onClick={onClose}>
            {t('overseer_app.layouts.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={onSave}>
            {t('overseer_app.layouts.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeclineModal}
        onClose={() => setOpenDeclineModal(false)}
        fullWidth={true}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('overseer_app.mapping.decline_job', 'Decline Job')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="dialog-description">
            {t(
              'overseer_app.mapping.decline_job',
              'Select the option to update the current job'
            )}
          </DialogContentText>
          <FormControl>
            <FormLabel id="demo-controlled-radio-buttons-group">
              Job State
            </FormLabel>
            <RadioGroup
              name="controlled-radio-buttons-group"
              value={declineOption}
              onChange={(e) => setDeclineOption(e.target.value)}
            >
              <FormControlLabel
                value="rejected"
                control={<Radio />}
                label="Rejected"
              />
              <FormControlLabel
                value="pending"
                control={<Radio />}
                label="Pending"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setOpenDeclineModal(false)}>
            {t('overseer_app.layouts.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onDecline}
            autoFocus
          >
            {t('overseer_app.layouts.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
